/**
 * Contacts: the user's own + company-shared (editable, stored by the server) and "NGN Customers"
 * (read-only, synced from billing/Xero). One store, one lookup used everywhere a number is shown.
 */
import { useSyncExternalStore } from 'react';
import { sameNumber, toE164AU } from './phone';

export interface ContactNumber {
  label: string;
  number: string;
}

export interface Contact {
  id: string;
  scope: 'personal' | 'shared';
  name: string;
  company: string;
  numbers: ContactNumber[];
  updatedAt: number;
  updatedBy: string;
}

export interface Customer {
  id: string;
  name: string;
  company: string;
  numbers: ContactNumber[];
  email: string;
}

/** Anything that can be shown in the contacts list. */
export type Entry =
  | { kind: 'personal' | 'shared'; id: string; name: string; company: string; numbers: ContactNumber[]; contact: Contact }
  | { kind: 'customer'; id: string; name: string; company: string; numbers: ContactNumber[]; email: string };

export interface ContactInput {
  scope: 'personal' | 'shared';
  name: string;
  company: string;
  numbers: ContactNumber[];
}

interface State {
  loaded: boolean;
  entries: Entry[];
  customersSyncedAt: number | null;
  error: string;
}

const REFRESH_MS = 60_000;
export const NUMBER_LABELS = ['mobile', 'work', 'main', 'home', 'other'];

function byName(a: Entry, b: Entry) {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

class ContactsStore {
  private state: State = { loaded: false, entries: [], customersSyncedAt: null, error: '' };
  private listeners = new Set<() => void>();
  private timer: number | undefined;
  /** canonical number → entry, rebuilt on every load */
  private index = new Map<string, Entry>();

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    if (this.listeners.size === 1) this.start();
    return () => {
      this.listeners.delete(fn);
      if (this.listeners.size === 0) this.stop();
    };
  };

  getSnapshot = () => this.state;

  private set(patch: Partial<State>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  private onFocus = () => void this.load();

  private start() {
    void this.load();
    this.timer = window.setInterval(() => void this.load(), REFRESH_MS);
    window.addEventListener('focus', this.onFocus);
  }

  private stop() {
    window.clearInterval(this.timer);
    window.removeEventListener('focus', this.onFocus);
  }

  async load(): Promise<void> {
    try {
      const r = await fetch('/api/contacts', { credentials: 'same-origin', cache: 'no-store' });
      if (!r.ok) throw new Error(String(r.status));
      const body = (await r.json()) as { contacts: Contact[]; customers: { items: Customer[]; syncedAt: number | null } };
      const entries: Entry[] = [
        ...body.contacts.map((c) => ({ kind: c.scope, id: c.id, name: c.name, company: c.company, numbers: c.numbers, contact: c }) as Entry),
        ...body.customers.items.map((c) => ({ kind: 'customer', id: c.id, name: c.name, company: c.company, numbers: c.numbers, email: c.email }) as Entry),
      ].sort(byName);
      // Personal beats shared beats customer when the same number appears in several places.
      const rank = { personal: 0, shared: 1, customer: 2 } as const;
      const index = new Map<string, Entry>();
      for (const e of [...entries].sort((a, b) => rank[a.kind] - rank[b.kind])) {
        for (const n of e.numbers) {
          const key = toE164AU(n.number);
          if (!index.has(key)) index.set(key, e);
        }
      }
      this.index = index;
      this.set({ loaded: true, entries, customersSyncedAt: body.customers.syncedAt, error: '' });
    } catch {
      this.set({ loaded: true, error: 'Contacts are unavailable right now.' });
    }
  }

  /** The contact a number belongs to, if any. */
  lookup(number: string | undefined): Entry | undefined {
    if (!number) return undefined;
    return this.index.get(toE164AU(number));
  }

  private async send(method: string, url: string, body?: unknown): Promise<Contact | null> {
    const r = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: body ? { 'content-type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `Request failed (${r.status})`);
    const out = r.status === 204 ? null : ((await r.json()) as Contact);
    await this.load();
    return out;
  }

  create(input: ContactInput) {
    return this.send('POST', '/api/contacts', input);
  }

  update(id: string, input: Omit<ContactInput, 'scope'>) {
    return this.send('PUT', `/api/contacts/${encodeURIComponent(id)}`, input);
  }

  async remove(id: string) {
    await this.send('DELETE', `/api/contacts/${encodeURIComponent(id)}`);
  }

  /** Add a number to an existing editable contact (no-op if it's already there). */
  addNumber(contact: Contact, number: ContactNumber) {
    if (contact.numbers.some((n) => sameNumber(n.number, number.number))) return Promise.resolve(contact);
    return this.update(contact.id, { name: contact.name, company: contact.company, numbers: [...contact.numbers, number] });
  }
}

export const contacts = new ContactsStore();

export function useContacts() {
  return useSyncExternalStore(contacts.subscribe, contacts.getSnapshot);
}

/** Resolve a number to a contact, re-rendering when contacts change. */
export function useContactFor(number: string | undefined): Entry | undefined {
  useContacts();
  return contacts.lookup(number);
}

export function initials(name: string): string {
  const parts = name.replace(/[^\p{L}\p{N} ]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return ((parts[0][0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/** Case/diacritic-insensitive match on name, company or number digits. */
export function matches(e: Entry, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const digits = needle.replace(/\D/g, '');
  const text = `${e.name} ${e.company}`.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
  if (text.includes(needle.normalize('NFD').replace(/\p{M}/gu, ''))) return true;
  if (digits.length >= 3) return e.numbers.some((n) => n.number.replace(/\D/g, '').includes(digits) || `0${n.number.replace(/^\+61/, '')}`.includes(digits));
  return false;
}
